import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams, Link, Routes, Route, Navigate } from 'react-router-dom';
import { Search, Users, Bell, TrendingUp, MapPin, Calendar, ArrowLeft, Filter, ExternalLink, Clock, Flag, Info, Newspaper, Tag, Moon, Sun, FileText, Gavel, Landmark, Scale, UserCheck, Vote, Globe, Home, Building, MessageCircle, Share2, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import InfoTooltip from '../components/InfoTooltip';
import Bills from './Bills';
import LocalBody from './LocalBody';
import CalendarPage from './Calendar';
import About from './About';
import PoliticianProfile from './PoliticianProfile';
import VotingRecords from './VotingRecords';
import SocialMediaPosts from './SocialMediaPosts';
import BillDetail from './BillDetail';
import { buildApiUrl } from '../config';
import { parseLocalDate } from '../scripts/bills/billUtils';
import {
  POLITICAL_PARTIES,
  DEFAULT_VALUES,
  GROUPING_OPTIONS,
  PROFILE_VIEW_MODES,
  FILTER_OPTIONS,
  FALLBACK_MESSAGES,
  getEmptyBioMessage
} from '../constants';
import { getPolicyTagIcon, getPolicyTagColor } from '../scripts/bills/policyTagUtils';
import CommitteeCard from '../components/CommitteeCard';
import { getSocialLinkIcon } from '../components/SocialLinkIcons';
import SponsoredBills from './SponsoredBills';
import UserMenu from '../components/UserMenu';
import SignInModal from './SignInModal';
import Account from './Account';
import ResetPassword from './ResetPassword';
import ScrollToTop from '../components/ScrollToTop';
import DevelopmentBanner from '../components/DevelopmentBanner';
import BugReport from '../components/BugReport';
import AppNavbar from '../components/AppNavbar';
import { getPostHog, trackEvent } from '../lib/posthog';
import Homepage from './Homepage';
import useFeatureControls from '../hooks/useFeatureControls';

// Wrapper component to conditionally render background based on returnUrl
const BillDetailWithBackground = ({ onBackToHome, isDarkMode, setIsDarkMode, featureControls }) => {
  const location = useLocation();
  const { billNumber } = useParams();
  const returnUrl = location.state?.returnUrl || '/bills';
  const showBillDetail = Boolean(billNumber);
  
  return (
    <>
      {showBillDetail && returnUrl === '/account' ? (
        <Account isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
      ) : (
        <Bills 
          onBackToHome={onBackToHome} 
          isDarkMode={isDarkMode} 
          setIsDarkMode={setIsDarkMode}
          featureControls={featureControls}
        />
      )}
      {showBillDetail && (
        <BillDetail isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
      )}
    </>
  );
};

// Add font import and custom scrollbar styles
const fontStyle = document.createElement('style');
fontStyle.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  /* Custom Scrollbar Styles - Blue Red Gradient */
  
  /* WebKit browsers (Chrome, Safari, Edge) */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    -webkit-appearance: none;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
    border: none;
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    -webkit-border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%);
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* Safari specific overrides */
  @supports (-webkit-backdrop-filter: none) {
    ::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
    }
    
    ::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
    
    ::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%) !important;
    }
  }

  /* Dark mode scrollbar */
  .dark ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #f87171 100%);
  }

  .dark ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: #3b82f6 transparent;
  }

  .dark * {
    scrollbar-color: #60a5fa transparent;
  }

  /* Custom scrollbar for specific elements */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    -webkit-appearance: none;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    border: none;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
    border-radius: 8px;
    border: 1px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    transform: scale(1.05);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #f87171 100%);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
  }

  /* Modal scrollbar */
  .modal-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    -webkit-appearance: none;
  }

  .modal-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 10px;
    border: none;
  }

  .modal-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.03);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #f87171 100%);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
  }

  /* Force scrollbar styling in Safari */
  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    ::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 8px;
      height: 8px;
    }
    
    ::-webkit-scrollbar-thumb {
      -webkit-appearance: none;
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
      border-radius: 10px;
      border: 2px solid transparent;
      background-clip: content-box;
      -webkit-background-clip: content-box;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 6px;
      height: 6px;
    }
    
    .modal-scrollbar::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 8px;
      height: 8px;
    }
  }

  /* Additional Safari-specific rules */
  @supports (-webkit-touch-callout: none) {
    ::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
      -webkit-appearance: none !important;
    }
    
    ::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
      -webkit-appearance: none !important;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px !important;
      height: 6px !important;
      -webkit-appearance: none !important;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 8px !important;
      border: 1px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
    
    .modal-scrollbar::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
      -webkit-appearance: none !important;
    }
    
    .modal-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
  }

  /* Party-specific scrollbar styles */
  .democrat-scrollbar::-webkit-scrollbar {
    width: 6px !important;
    height: 6px !important;
    -webkit-appearance: none !important;
  }

  .democrat-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05) !important;
    border-radius: 8px !important;
    border: none !important;
  }

  .democrat-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%) !important;
    border-radius: 8px !important;
    border: 1px solid transparent !important;
    background-clip: content-box !important;
    -webkit-background-clip: content-box !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.3s ease !important;
  }

  .democrat-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%) !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
    transform: scale(1.05) !important;
  }

  .dark .democrat-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05) !important;
  }

  .dark .democrat-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%) !important;
  }

  .dark .democrat-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  }

  .republican-scrollbar::-webkit-scrollbar {
    width: 6px !important;
    height: 6px !important;
    -webkit-appearance: none !important;
  }

  .republican-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05) !important;
    border-radius: 8px !important;
    border: none !important;
  }

  .republican-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%) !important;
    border-radius: 8px !important;
    border: 1px solid transparent !important;
    background-clip: content-box !important;
    -webkit-background-clip: content-box !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.3s ease !important;
  }

  .republican-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%) !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
    transform: scale(1.05) !important;
  }

  .dark .republican-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05) !important;
  }

  .dark .republican-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #f87171 0%, #ef4444 100%) !important;
  }

  .dark .republican-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  }

  /* Firefox scrollbar for party-specific elements */
  .democrat-scrollbar {
    scrollbar-width: thin !important;
    scrollbar-color: #3b82f6 transparent !important;
  }

  .dark .democrat-scrollbar {
    scrollbar-color: #60a5fa transparent !important;
  }

  .republican-scrollbar {
    scrollbar-width: thin !important;
    scrollbar-color: #ef4444 transparent !important;
  }

  .dark .republican-scrollbar {
    scrollbar-color: #f87171 transparent !important;
  }
`;
document.head.appendChild(fontStyle);

// PoliticianCard component to handle async latest activity
const PoliticianCard = ({
  politician,
  selectedHomepageTag,
  isDarkMode,
  getPartyColor,
  getPartyAccentClass,
  getPartyBorderClass,
  getBranchClassification,
  getLatestActivityForTag,
  handlePoliticianClick,
  DEFAULT_VALUES,
  FALLBACK_MESSAGES
}) => {
  const [latestActivity, setLatestActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Normalize server-provided vote activity into: Voted "<Vote>" on <Title>
    const formatServerActivitySummary = (summary, tags) => {
      if (!summary || typeof summary !== 'string') return summary;
      const hasVotingTag = Array.isArray(tags) && tags.includes('Voting Record');
      // Matches: "Voted Yea — Title", "Voted Nay — Title", also handles hyphen-minus
      const voteDashRegex = /^Voted\s+([A-Za-z ]+)\s+[—-]\s+(.+)\s*$/i;
      const match = summary.match(voteDashRegex);
      if (hasVotingTag && match) {
        const voteText = match[1].trim();
        const title = match[2].trim();
        return `Voted "${voteText}" on ${title}`;
      }
      return summary;
    };

    const fetchLatestActivity = () => {
      setLoading(true);
      try {
        const activity = getLatestActivityForTag(politician.id, selectedHomepageTag);
        if (activity) {
          setLatestActivity(activity);
        } else {
          // Fallback: use server-provided recentActivity from politicians list (Supabase-backed),
          // so first page load shows activity immediately without extra calls
          const summary = politician.recentActivity;
          const date = politician.recentActivityDate;
          const tags = Array.isArray(politician.recentActivityTags) ? politician.recentActivityTags : [];
          // If a specific tag is selected, only show fallback when it matches the activity's tags
          const tagMatches =
            selectedHomepageTag === 'all' ||
            (tags && tags.includes(selectedHomepageTag));

          if (summary && tagMatches) {
            const formattedSummary = formatServerActivitySummary(summary, tags);
            const formattedDate = date
              ? (() => {
                  const dateObj = parseLocalDate(date);
                  return dateObj && !Number.isNaN(dateObj.getTime())
                    ? dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                    : null;
                })()
              : null;
            setLatestActivity({
              id: `fallback-${politician.id}`,
              type: tags.includes('Voting Record') ? 'vote' : 'fallback',
              summary: formattedSummary,
              date: date || null,
              formattedDate,
              tags
            });
          } else {
            setLatestActivity(null);
          }
        }
      } catch (error) {
        console.error('Error fetching latest activity:', error);
        setLatestActivity(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestActivity();
  }, [politician.id, selectedHomepageTag, getLatestActivityForTag]);

  return (
    <div
      onClick={() => handlePoliticianClick(politician)}
      className={`group relative ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer ${politician.party === 'Democrat'
          ? isDarkMode ? 'hover:border-blue-800' : 'hover:border-blue-200'
          : politician.party === 'Republican'
            ? isDarkMode ? 'hover:border-red-800/50' : 'hover:border-red-200'
            : isDarkMode ? 'hover:border-gray-600' : 'hover:border-gray-200'
        } border-2 border-transparent`}
    >
      <div className="p-4 md:p-6">
        <div className="flex flex-col items-center text-center">
          <img
            src={politician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(politician.name)}&background=random&color=fff`}
            alt={politician.name}
            className={`w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 mb-3 ${getPartyBorderClass(politician.party)}`}
          />
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 ${getPartyColor(politician.party)}`}>
            {politician.party}
          </span>
          <h3 className={`text-base md:text-lg font-semibold mb-2 ${getPartyAccentClass(politician.party)} leading-tight`}>{politician.name}</h3>
          <p className={`text-xs md:text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1 leading-tight`}>{politician.office || FALLBACK_MESSAGES.NO_OFFICE_INFO}</p>
          {/* Branch pill under position title, above location */}
          {(() => {
            const branchInfo = getBranchClassification(politician.office, politician.branch || null);
            if (!branchInfo) return null;
            return (
              <span className={`inline-flex items-center px-2 py-0.5 md:px-2.5 rounded-full text-xs font-medium mb-2 border ${branchInfo.color}`}
                style={{ fontWeight: 500 }}>
                <span className="w-3 h-3 md:w-4 md:h-4 mr-1">{branchInfo.icon}</span>
                <span className="text-xs">{branchInfo.branch}</span>
              </span>
            );
          })()}
          {/* Locality pill */}
          {(() => {
            const loc = politician.locality || DEFAULT_VALUES.NA;
            const icon = <MapPin className="w-3 h-3 md:w-4 md:h-4 mr-1" />;
            const color = isDarkMode ? 'bg-slate-700/50 text-slate-300 border-slate-500/30' : 'bg-slate-100 text-slate-700 border-slate-200';
            return (
              <span className={`inline-flex items-center px-2 py-0.5 md:px-2.5 rounded-full text-xs font-medium mb-2 border ${color}`} style={{ fontWeight: 500 }}>
                {icon}
                <span className="text-xs">{loc}</span>
              </span>
            );
          })()}
          <div className="flex items-center justify-center text-xs md:text-sm mb-4">
            <MapPin className={`w-3 h-3 md:w-4 md:h-4 mr-1 ${getPartyAccentClass(politician.party)}`} />
            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} leading-tight`}>{politician.location || FALLBACK_MESSAGES.NO_LOCATION_INFO}</span>
          </div>
        </div>

        <div
          className={`mt-3 md:mt-4 p-3 md:p-4 rounded-lg border transition-colors ${politician.party === 'Democrat'
              ? isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-800 hover:from-blue-900/50 hover:to-blue-800/30' : 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-100 hover:from-blue-100 hover:to-blue-200/50'
              : politician.party === 'Republican'
                ? isDarkMode ? 'bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-800 hover:from-red-900/50 hover:to-red-800/30' : 'bg-gradient-to-br from-red-50 to-red-100/50 border-red-100 hover:from-red-100 hover:to-red-200/50'
                : isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
            } ${latestActivity && (latestActivity.type === 'vote' || (latestActivity.tags || []).includes('Voting Record')) ? 'cursor-pointer' : ''}`}
          onClick={(e) => {
            const isVoting = latestActivity && (latestActivity.type === 'vote' || (latestActivity.tags || []).includes('Voting Record'));
            if (!isVoting) return;
            e.stopPropagation();
            const tagToApply = selectedHomepageTag !== DEFAULT_VALUES.ALL ? selectedHomepageTag : undefined;
            handlePoliticianClick(politician, { viewMode: 'voting', tag: tagToApply });
          }}
        >
          <div className={`flex items-center justify-between text-xs md:text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
            <div className="flex items-center">
              <TrendingUp className={`w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 ${getPartyAccentClass(politician.party)}`} />
              <span className="hidden sm:inline">Latest Activity</span>
              <span className="sm:hidden">Activity</span>
            </div>
            {loading ? (
              <div className={`w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin`}></div>
            ) : latestActivity ? (
              <div className={`flex items-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <Calendar className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">{latestActivity.formattedDate}</span>
                <span className="sm:hidden">{latestActivity.formattedDate?.split(' ')[0]}</span>
              </div>
            ) : null}
          </div>
          {loading ? (
            <div className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} leading-tight`}>
              Loading activity...
            </div>
          ) : latestActivity ? (
            <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-2 md:line-clamp-3 leading-tight`}>
              {latestActivity.summary}
            </p>
          ) : (
            <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} leading-tight`}>
              {selectedHomepageTag === DEFAULT_VALUES.ALL ? FALLBACK_MESSAGES.NO_RECENT_ACTIVITY : `${FALLBACK_MESSAGES.NO_RECENT_ACTIVITY_IN_TAG} ${selectedHomepageTag}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CivicDutyApp = () => {
  // Feature flag: Set to true when profiles are fully populated
  const PROFILES_ENABLED = true;
  const { features: featureControls, loading: featureControlsLoading } = useFeatureControls();
  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState('home');
  const [selectedPolitician, setSelectedPolitician] = useState(null);
  const [selectedTag, setSelectedTag] = useState(DEFAULT_VALUES.ALL);
  const [selectedHomepageTag, setSelectedHomepageTag] = useState(DEFAULT_VALUES.ALL);
  const [expandedUpdate, setExpandedUpdate] = useState(null);
  const [aboutPersonExpanded, setAboutPersonExpanded] = useState(true);
  const [aboutPositionExpanded, setAboutPositionExpanded] = useState(true);
  const [politicians, setPoliticians] = useState([]);
  const [politicianUpdates, setPoliticianUpdates] = useState({});
  const [politicianVotingRecords, setPoliticianVotingRecords] = useState({});
  const [politicianVoteSummaries, setPoliticianVoteSummaries] = useState({});
  const [politicianCommittees, setPoliticianCommittees] = useState({});
  const [committeesLoading, setCommitteesLoading] = useState(false);


  const [policyTags, setPolicyTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [members, setMembers] = useState([]);
  const [showPressReleases, setShowPressReleases] = useState(false);
  const [pressReleases, setPressReleases] = useState([]);
  const [pressLoading, setPressLoading] = useState(false);
  const [pressError, setPressError] = useState(null);
  const [pressFilter, setPressFilter] = useState(FILTER_OPTIONS.ALL);
  const [showPressModal, setShowPressModal] = useState(false);
  const [selectedPressRelease, setSelectedPressRelease] = useState(null);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [tagError, setTagError] = useState(null);
  const [showMoreTags, setShowMoreTags] = useState(false);
  const [showTagEditModal, setShowTagEditModal] = useState(false);
  const [editingTags, setEditingTags] = useState([]);
  const [tagEditLoading, setTagEditLoading] = useState(false);
  const [tagEditError, setTagEditError] = useState(null);
  const [showTagDeleteModal, setShowTagDeleteModal] = useState(false);
  const [deletingTag, setDeletingTag] = useState(null);
  const [tagDeleteLoading, setTagDeleteLoading] = useState(false);
  const [tagDeleteError, setTagDeleteError] = useState(null);
  const [showTagAddModal, setShowTagAddModal] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagAddLoading, setTagAddLoading] = useState(false);
  const [tagAddError, setTagAddError] = useState(null);
  const [showTagEditPressModal, setShowTagEditPressModal] = useState(false);
  const [editingPressTags, setEditingPressTags] = useState([]);
  const [tagEditPressLoading, setTagEditPressLoading] = useState(false);
  const [tagEditPressError, setTagEditPressError] = useState(null);
  const [showTagDeletePressModal, setShowTagDeletePressModal] = useState(false);
  const [deletingPressTag, setDeletingPressTag] = useState(null);
  const [tagDeletePressLoading, setTagDeletePressLoading] = useState(false);
  const [tagDeletePressError, setTagDeletePressError] = useState(null);
  const [showTagAddPressModal, setShowTagAddPressModal] = useState(false);
  const [newPressTag, setNewPressTag] = useState('');
  const [tagAddPressLoading, setTagAddPressLoading] = useState(false);
  const [tagAddPressError, setTagAddPressError] = useState(null);
  const [showTagEditPressReleaseModal, setShowTagEditPressReleaseModal] = useState(false);
  const [editingPressReleaseTags, setEditingPressReleaseTags] = useState([]);
  const [tagEditPressReleaseLoading, setTagEditPressReleaseLoading] = useState(false);
  const [tagEditPressReleaseError, setTagEditPressReleaseError] = useState(null);
  const [showTagDeletePressReleaseModal, setShowTagDeletePressReleaseModal] = useState(false);
  const [deletingPressReleaseTag, setDeletingPressReleaseTag] = useState(null);
  const [tagDeletePressReleaseLoading, setTagDeletePressReleaseLoading] = useState(false);
  const [tagDeletePressReleaseError, setTagDeletePressReleaseError] = useState(null);
  const [showTagAddPressReleaseModal, setShowTagAddPressReleaseModal] = useState(false);
  const [newPressReleaseTag, setNewPressReleaseTag] = useState('');
  const [tagAddPressReleaseLoading, setTagAddPressReleaseLoading] = useState(false);
  const [tagAddPressReleaseError, setTagAddPressReleaseError] = useState(null);
  const [showTagEditPressReleaseTagModal, setShowTagEditPressReleaseTagModal] = useState(false);
  const [editingPressReleaseTagTags, setEditingPressReleaseTagTags] = useState([]);
  const [tagEditPressReleaseTagLoading, setTagEditPressReleaseTagLoading] = useState(false);
  const [tagEditPressReleaseTagError, setTagEditPressReleaseTagError] = useState(null);
  const [showTagDeletePressReleaseTagModal, setShowTagDeletePressReleaseTagModal] = useState(false);
  const [deletingPressReleaseTagTag, setDeletingPressReleaseTagTag] = useState(null);
  const [tagDeletePressReleaseTagLoading, setTagDeletePressReleaseTagLoading] = useState(false);
  const [tagDeletePressReleaseTagError, setTagDeletePressReleaseTagError] = useState(null);
  const [showTagAddPressReleaseTagModal, setShowTagAddPressReleaseTagModal] = useState(false);
  const [newPressReleaseTagTag, setNewPressReleaseTagTag] = useState('');
  const [tagAddPressReleaseTagLoading, setTagAddPressReleaseTagLoading] = useState(false);
  const [tagAddPressReleaseTagError, setTagAddPressReleaseTagError] = useState(null);
  const [profileViewMode, setProfileViewMode] = useState(PROFILE_VIEW_MODES.UPDATES);
  const [groupBy, setGroupBy] = useState(GROUPING_OPTIONS.NONE);
  // Tab keys that control visibility: press_releases (Updates), social_media, voting, committees, sponsored
  const profileTabKeyByMode = useMemo(() => ({
    [PROFILE_VIEW_MODES.UPDATES]: 'press_releases',
    [PROFILE_VIEW_MODES.SOCIAL_MEDIA]: 'social_media',
    voting: 'voting',
    [PROFILE_VIEW_MODES.COMMITTEES]: 'committees',
    [PROFILE_VIEW_MODES.SPONSORED]: 'sponsored'
  }), []);
  const visibleProfileTabs = useMemo(() => {
    const tabs = selectedPolitician?.profile_tabs;
    return Array.isArray(tabs) && tabs.length > 0 ? tabs : ['press_releases', 'social_media'];
  }, [selectedPolitician?.profile_tabs]);
  const showProfileTab = useMemo(() => (mode) => {
    const key = profileTabKeyByMode[mode] ?? mode;
    return visibleProfileTabs.includes(key);
  }, [visibleProfileTabs, profileTabKeyByMode]);
  const modeByTabKey = useMemo(() => ({
    press_releases: PROFILE_VIEW_MODES.UPDATES,
    social_media: PROFILE_VIEW_MODES.SOCIAL_MEDIA,
    voting: 'voting',
    committees: PROFILE_VIEW_MODES.COMMITTEES,
    sponsored: PROFILE_VIEW_MODES.SPONSORED
  }), []);
  useEffect(() => {
    if (!selectedPolitician) return;
    const key = profileTabKeyByMode[profileViewMode] ?? profileViewMode;
    if (visibleProfileTabs.includes(key)) return;
    const firstKey = visibleProfileTabs[0];
    if (firstKey && modeByTabKey[firstKey] != null) {
      setProfileViewMode(modeByTabKey[firstKey]);
    }
  }, [selectedPolitician, visibleProfileTabs, profileViewMode, profileTabKeyByMode, modeByTabKey]);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const resolvedFeatureControls = useMemo(() => ({
    home: true,
    about: true,
    bills: true,
    calendar: true,
    politicalProfiles: true,
    localBody: true,
    accounts: true,
    ...(featureControls || {})
  }), [featureControls]);
  const fallbackRoute = useMemo(() => {
    if (resolvedFeatureControls.home) return '/';
    if (resolvedFeatureControls.politicalProfiles) return '/politicians';
    if (resolvedFeatureControls.about) return '/about';
    if (resolvedFeatureControls.bills) return '/bills';
    if (resolvedFeatureControls.calendar) return '/calendar';
    if (resolvedFeatureControls.localBody) return '/local-body/warner-robins';
    return '/';
  }, [resolvedFeatureControls]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Track route changes in PostHog for session recordings
  useEffect(() => {
    const posthog = getPostHog();
    if (posthog && location.pathname) {
      // Capture pageview for React Router navigation
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        $pathname: location.pathname,
      });
    }
  }, [location.pathname]);

  // Keep UI state in sync with URL
  useEffect(() => {
    const path = location.pathname || '/';
    if (path.startsWith('/bills')) {
      setCurrentView('bills');
      setSelectedPolitician(null);
      return;
    }
    if (path.startsWith('/politicians/')) {
      const id = path.split('/')[2];
      if (id && politicians && politicians.length > 0) {
        const match = politicians.find(p => String(p.id) === String(id) || String(p._id) === String(id));
        setSelectedPolitician(match || null);
      }
      setCurrentView('profile');
      return;
    }
    setCurrentView('home');
    setSelectedPolitician(null);
  }, [location.pathname, politicians]); // Removed location.search as it was causing unnecessary re-renders

  useEffect(() => {
    fetchPoliticians();
    fetchPolicyTags();
  }, []);

  useEffect(() => {
    if (politicians.length > 0) {
      fetchAllPoliticianUpdates();
      // Skip legacy vote preloading until Supabase vote summaries are implemented
    }
  }, [politicians]);

  useEffect(() => {
    if (selectedPolitician) {
      if (!politicianUpdates[selectedPolitician.id]) {
        fetchPoliticianUpdates(selectedPolitician.id);
      }


    }
  }, [selectedPolitician]);

  // Ensure profile view starts at top on mobile
  useEffect(() => {
    if (currentView === 'profile' && selectedPolitician) {
      const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
      if (isMobile) {
        // Use multiple methods to guarantee top on mobile browsers
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        if (document && document.documentElement) {
          document.documentElement.scrollTop = 0;
        }
        if (document && document.body) {
          document.body.scrollTop = 0;
        }
        // Run once more after paint in case layout shifts push content
        setTimeout(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }, 0);
      }
    }
  }, [currentView, selectedPolitician]);

  // Lazy-load committees for selected politician when profile opens
  useEffect(() => {
    const politicianId = selectedPolitician?.id;
    if (!politicianId) return;
    if (politicianCommittees[politicianId] !== undefined) return;
    fetchPoliticianCommittees(politicianId);
  }, [selectedPolitician, politicianCommittees]);

  const fetchPolicyTags = async () => {
    try {
      setTagLoading(true);
      const response = await fetch(buildApiUrl('/api/policy-tags'));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const allTags = await response.json();
      // Filter for tags with scope "universal" only (or tags without scope field, for backward compatibility)
      const universalTags = allTags.filter(tag => !tag.scope || tag.scope === 'universal');
      console.log('📋 Loaded universal policy tags from database:', universalTags.map(t => t.name));
      setAvailableTags(universalTags);
    } catch (err) {
      console.error('❌ Error fetching policy tags:', err);
      setAvailableTags([]);
    } finally {
      setTagLoading(false);
    }
  };

  const fetchPoliticians = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl('/api/politicians'));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Loaded politicians from MongoDB:', data);
      setPoliticians(data);

      // Prefetch which politicians have votes to avoid first-click delay rendering the Voting tab
      try {
        const ids = (data || []).map(p => p.id).filter(Boolean);
        const chunkSize = 200;
        const chunks = [];
        for (let i = 0; i < ids.length; i += chunkSize) {
          chunks.push(ids.slice(i, i + chunkSize));
        }
        const results = await Promise.all(chunks.map(async (chunk) => {
          const param = encodeURIComponent(chunk.join(','));
          const res = await fetch(buildApiUrl(`/api/politicians/with-votes?ids=${param}`));
          if (!res.ok) return { politicianIds: [] };
          return res.json().catch(() => ({ politicianIds: [] }));
        }));
        const idsWithVotes = new Set(results.flatMap(r => r.politicianIds || []));
        setPoliticianVotingRecords(prev => {
          const updated = { ...prev };
          ids.forEach(id => {
            if (updated[id] === undefined) {
              updated[id] = idsWithVotes.has(id) ? 1 : 0;
            }
          });
          return updated;
        });
      } catch (prefetchErr) {
        console.warn('⚠️ Failed prefetching politicians-with-votes; falling back to on-demand count.', prefetchErr);
      }
    } catch (err) {
      console.error('❌ Error fetching politicians:', err);
      setError('Failed to load politicians. Please check your internet connection and try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPoliticianUpdates = async (politicianId, skipLoading = false) => {
    try {
      if (!skipLoading) {
        setUpdatesLoading(true);
      }

      const response = await fetch(buildApiUrl(`/api/politicians/${politicianId}/updates`));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updates = await response.json();
      console.log(`📰 Loaded ${updates.length} approved updates for politician ${politicianId}:`, updates);

      setPoliticianUpdates(prev => ({
        ...prev,
        [politicianId]: updates
      }));
    } catch (err) {
      console.error('❌ Error fetching politician updates:', err);
      setPoliticianUpdates(prev => ({
        ...prev,
        [politicianId]: []
      }));
    } finally {
      if (!skipLoading) {
        setUpdatesLoading(false);
      }
    }
  };
  const fetchPoliticianCommittees = async (politicianId) => {
    try {
      setCommitteesLoading(true);
      const response = await fetch(buildApiUrl(`/api/politicians/${politicianId}/committees`));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const committees = await response.json();
      setPoliticianCommittees(prev => ({
        ...prev,
        [politicianId]: committees || []
      }));
    } catch (err) {
      console.error('❌ Error fetching politician committees:', err);
      setPoliticianCommittees(prev => ({
        ...prev,
        [politicianId]: []
      }));
    } finally {
      setCommitteesLoading(false);
    }
  };

  const fetchAllPoliticianUpdates = async () => {
    try {
      console.log('🔄 Fetching updates for all politicians...');
      const updatePromises = politicians.map(politician =>
        fetchPoliticianUpdates(politician.id, true) // Use 'id' instead of '_id'
      );

      await Promise.all(updatePromises);
      console.log('✅ Finished fetching updates for all politicians');
      console.log('📊 Current politicianUpdates state:', Object.keys(politicianUpdates).map(id => ({
        politicianId: id,
        updateCount: politicianUpdates[id]?.length || 0
      })));
    } catch (error) {
      console.error('❌ Error fetching all politician updates:', error);
    }
  };

  const getPartyColor = (party) => {
    if (!party) return isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    return party.toLowerCase() === POLITICAL_PARTIES.REPUBLICAN
      ? isDarkMode ? 'bg-gradient-to-r from-red-500 to-red-700 text-white' : 'bg-gradient-to-r from-red-400 to-red-600 text-white'
      : party.toLowerCase() === POLITICAL_PARTIES.DEMOCRAT
        ? isDarkMode ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white' : 'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
  };

  const getPartyGradientBg = (party) => {
    if (!party) return isDarkMode ? 'bg-gray-700' : 'bg-gray-100';
    return party.toLowerCase() === POLITICAL_PARTIES.REPUBLICAN
      ? isDarkMode ? 'bg-gradient-to-br from-red-500 to-red-700' : 'bg-gradient-to-br from-red-400 to-red-600'
      : party.toLowerCase() === POLITICAL_PARTIES.DEMOCRAT
        ? isDarkMode ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-blue-400 to-blue-600'
        : isDarkMode ? 'bg-gray-700' : 'bg-gray-100';
  };

  const getPartyButtonClass = (party, isSelected = false) => {
    if (!party) return isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200';

    if (isSelected) {
      return party.toLowerCase() === POLITICAL_PARTIES.REPUBLICAN
        ? 'bg-gradient-to-r from-red-500 to-red-700 text-white shadow-md'
        : party.toLowerCase() === POLITICAL_PARTIES.DEMOCRAT
          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-md'
          : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }

    return isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200';
  };

  const getPartyBorderClass = (party) => {
    if (!party) return 'border-gray-500';
    return party.toLowerCase() === 'republican'
      ? 'border-red-500'
      : party.toLowerCase() === 'democrat'
        ? 'border-blue-500'
        : 'border-gray-500';
  };

  const getPartyAccentClass = (party) => {
    if (!party) return isDarkMode ? 'text-gray-400' : 'text-gray-600';
    return party.toLowerCase() === 'republican'
      ? isDarkMode ? 'text-red-400' : 'text-red-600'
      : party.toLowerCase() === 'democrat'
        ? isDarkMode ? 'text-blue-400' : 'text-blue-600'
        : isDarkMode ? 'text-gray-400' : 'text-gray-600';
  };

  const getPartyWebsiteButtonClass = (party) => {
    if (!party) return 'bg-gray-500 hover:bg-gray-600';
    return party.toLowerCase() === 'republican'
      ? 'bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800'
      : party.toLowerCase() === 'democrat'
        ? 'bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800'
        : 'bg-gray-500 hover:bg-gray-600';
  };

  const getPartyProfileBg = (party) => {
    if (!party) return isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50';
    return party.toLowerCase() === 'republican'
      ? isDarkMode ? 'bg-gradient-to-br from-red-900/30 to-red-800/20' : 'bg-gradient-to-br from-red-50 via-white to-red-50'
      : party.toLowerCase() === 'democrat'
        ? isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20' : 'bg-gradient-to-br from-blue-50 via-white to-blue-50'
        : isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50';
  };



  const getTagColor = (tag) => {
    const colors = {
      'Economy': isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800',
      'Education': isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800',
      'Healthcare': isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800',
      'Taxes & Government Spending': isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800',
      'Crime & Justice': isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800',
      'Firearm Policy': isDarkMode ? 'bg-pink-900/50 text-pink-300' : 'bg-pink-100 text-pink-800',
      'Foreign Policy & Defense': isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      'Environment & Climate': isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-800',
      'Energy': isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-800',
      'Disability': isDarkMode ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-800',
      'Family & Social Issues': isDarkMode ? 'bg-lime-900/50 text-lime-300' : 'bg-lime-100 text-lime-800',
      'Immigration & Border Security': isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-800',
      'Agriculture Policy': isDarkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-800',
      'Technology & Privacy': isDarkMode ? 'bg-fuchsia-900/50 text-fuchsia-300' : 'bg-fuchsia-100 text-fuchsia-800',
      'US Economy & Jobs': isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'Other': isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
    };
    return colors[tag] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const [year, month, day] = dateString.split('-').map(Number);
    return `${months[month - 1]} ${day}, ${year}`;
  };

  const hasUpdatesForTag = (politicianId, tag) => {
    try {
      const updates = politicianUpdates[politicianId] || [];
      console.log(`🔍 Checking updates for politician ${politicianId}, tag: ${tag}, updates count: ${updates.length}`);
      if (tag === 'all') {
        // All topics: consider either any press updates or any votes on record (from precomputed map)
        const hasUpdates = updates.length > 0;
        // Look up politician to see if we have any vote summaries at all
        const pol = politicians.find(p => String(p.id) === String(politicianId) || String(p._id) === String(politicianId));
        const hasAnyVoteSummaries = pol && pol.recentVoteSummariesByTag && Object.keys(pol.recentVoteSummariesByTag).length > 0;
        const result = hasUpdates || hasAnyVoteSummaries;
        console.log(`🔍 All topics selected, has updates: ${hasUpdates}, has votes: ${!!hasAnyVoteSummaries} -> ${result}`);
        return result;
      }
      const hasTagUpdates = updates.some(update => update.tags && Array.isArray(update.tags) && update.tags.includes(tag));
      // If no press releases for the tag, allow latest vote under that tag to qualify
      let hasTagVote = false;
      if (!hasTagUpdates) {
        const pol = politicians.find(p => String(p.id) === String(politicianId) || String(p._id) === String(politicianId));
        const voteSummariesByTag = pol && pol.recentVoteSummariesByTag ? pol.recentVoteSummariesByTag : null;
        hasTagVote = !!(voteSummariesByTag && voteSummariesByTag[tag] && voteSummariesByTag[tag].voteDate);
      }
      const result = hasTagUpdates || hasTagVote;
      console.log(`🔍 Tag ${tag} selected, has press updates: ${hasTagUpdates}, has vote fallback: ${hasTagVote} -> ${result}`);
      return result;
    } catch (error) {
      console.error('Error in hasUpdatesForTag:', error, { politicianId, tag });
      return false;
    }
  };

  const handleHomepageTagSelect = (tagName) => {
    try {
      console.log('Selecting homepage tag:', tagName);
      setSelectedHomepageTag(tagName);
    } catch (error) {
      console.error('Error in handleHomepageTagSelect:', error);
    }
  };

  // Function to check if a politician has voting records
  const hasVotingRecords = (politician) => {
    if (!politician?.id) return false;
    const count = politicianVotingRecords[politician.id];
    return typeof count === 'number' && count > 0;
  };

  useEffect(() => {
    const politicianId = selectedPolitician?.id;
    if (!politicianId || politicianVotingRecords[politicianId] !== undefined) return;

    const fetchVoteCount = async () => {
      const totalVotes = await fetchPoliticianVoteCount(politicianId);
      setPoliticianVotingRecords(prev => ({
        ...prev,
        [politicianId]: totalVotes
      }));

      if (totalVotes > 0 && profileViewMode === PROFILE_VIEW_MODES.UPDATES) {
        setProfileViewMode('voting');
      }
    };

    fetchVoteCount();
  }, [selectedPolitician, politicianVotingRecords, profileViewMode]);

  const filteredPoliticians = politicians.filter(politician => {
    try {
      if (!politician) return false;

      const matchesSearch =
        (politician.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (politician.office || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (politician.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (politician.party || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesParty = !selectedParty || politician.party === selectedParty;

      // For tag filtering: show politicians who have updates for the tag
      const hasUpdatesForSelectedTag = hasUpdatesForTag(politician.id, selectedHomepageTag);
      const matchesHomepageTag = selectedHomepageTag === 'all' || hasUpdatesForSelectedTag;

      return matchesSearch && matchesParty && matchesHomepageTag;
    } catch (error) {
      console.error('Error filtering politician:', error, { politician });
      return false;
    }
  });

  const currentUpdates = selectedPolitician ? (politicianUpdates[selectedPolitician.id] || [])
    .filter(update => {
      if (selectedTags.length === 0) return true;
      return update.tags && selectedTags.some(tag => update.tags.includes(tag));
    }) : [];

  const handlePoliticianClick = (politician, options = {}) => {
    const { viewMode, tag } = options || {};
    const currentPath = `${location.pathname || '/'}${location.search || ''}`;
    const returnTo = currentPath.startsWith('/politicians/') ? '/politicians' : currentPath;
    setSelectedPolitician(politician);
    setCurrentView('profile');
    navigate(`/politicians/${politician.id || politician._id}`, { state: { returnTo } });
    if (tag && tag !== 'all') {
      setSelectedTag(tag);
      setSelectedTags([tag]);
    } else {
      setSelectedTag(selectedHomepageTag === 'all' ? 'all' : selectedHomepageTag);
      setSelectedTags(selectedHomepageTag === 'all' ? [] : [selectedHomepageTag]);
    }
    setExpandedUpdate(null);
    setUpdatesLoading(false);

    // Default to updates view unless a specific viewMode is requested
    setProfileViewMode(viewMode || PROFILE_VIEW_MODES.UPDATES);

    setShowMoreTags(false); // Reset tag dropdown state

    // Scroll to top when navigating to profile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToHome = () => {
    const returnTo = location.state?.returnTo || '/';
    navigate(returnTo);
    setCurrentView('home');
    setSelectedPolitician(null);
    setExpandedUpdate(null);
    setSelectedTag('all');

    // Scroll to top when navigating back to home
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBillsClick = () => {
    navigate('/bills');
  };

  const handleUpdateClick = (update, e) => {
    e.stopPropagation();
    setExpandedUpdate({
      ...update,
      date: update.formattedDate
    });
  };

  const handleCloseModal = () => {
    setExpandedUpdate(null);
  };

  const handleTagToggle = (tagName) => {
    setSelectedTags(prev => {
      if (prev.includes(tagName)) {
        return [];
      }
      return [tagName];
    });
  };



  const getTagCounts = (politicianId) => {
    const updates = politicianUpdates[politicianId] || [];
    const counts = {};

    availableTags.forEach(tag => {
      counts[tag.name] = 0;
    });

    updates.forEach(update => {
      if (update.tags && Array.isArray(update.tags)) {
        update.tags.forEach(tag => {
          if (counts[tag] !== undefined) {
            counts[tag]++;
          }
        });
      }
    });

    return counts;
  };

  // Function to get sorted tags by frequency for a politician
  const getSortedTagsForPolitician = (politicianId) => {
    const tagCounts = getTagCounts(politicianId);
    const totalUpdates = (politicianUpdates[politicianId] || []).length;

    // Create array of tags with their counts
    const tagsWithCounts = availableTags.map(tag => ({
      ...tag,
      count: tagCounts[tag.name] || 0
    }));

    // Sort by frequency (descending), but keep "Other" last
    const sortedTags = tagsWithCounts.sort((a, b) => {
      // "Other" always goes last
      if (a.name === 'Other' && b.name !== 'Other') return 1;
      if (b.name === 'Other' && a.name !== 'Other') return -1;

      // Sort by count (descending)
      return b.count - a.count;
    });

    return {
      allTag: { name: 'All', count: totalUpdates },
      sortedTags: sortedTags,
      totalUpdates: totalUpdates
    };
  };

  const getLatestActivityForTag = (politicianId, tag) => {
    try {
      const updates = politicianUpdates[politicianId] || [];
      const pol = politicians.find(p => String(p.id) === String(politicianId) || String(p._id) === String(politicianId));
      const voteSummariesByTag = pol && pol.recentVoteSummariesByTag ? pol.recentVoteSummariesByTag : null;
      let latestActivity = null;

      if (tag === 'all') {
        // Collect all activities: press releases and votes
        const allActivities = [];

        // Add all press releases
        updates.forEach(update => {
          if (update.date) {
            allActivities.push({
              ...update,
              activityDate: parseLocalDate(update.date) || new Date(0)
            });
          }
        });

        // Add all votes from voteSummariesByTag
        if (voteSummariesByTag) {
          Object.keys(voteSummariesByTag).forEach(voteTag => {
            const voteSummary = voteSummariesByTag[voteTag];
            if (voteSummary && voteSummary.voteDate) {
              const dateObj = parseLocalDate(voteSummary.voteDate);
              if (dateObj && !Number.isNaN(dateObj.getTime())) {
                allActivities.push({
                  id: `vote-${voteSummary.voteDate}-${voteTag}`,
                  type: 'vote',
                  summary: `Voted "${voteSummary.voteText}" on ${voteSummary.displayTitle}`,
                  date: voteSummary.voteDate,
                  formattedDate: dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
                  tags: ['Voting Record', voteTag],
                  activityDate: dateObj
                });
              }
            }
          });
        }

        // Sort by date (most recent first) and get the latest
        if (allActivities.length > 0) {
          allActivities.sort((a, b) => {
            const dateA = a.activityDate || new Date(0);
            const dateB = b.activityDate || new Date(0);
            return dateB - dateA; // Most recent first
          });
          const { activityDate, ...activity } = allActivities[0];
          latestActivity = activity;
        }
      } else {
        // For specific tags, first try press releases
        latestActivity = updates.find(update => update.tags && Array.isArray(update.tags) && update.tags.includes(tag)) || null;

        // If no press release matches the tag, fallback to the latest voting record for that tag.
        // Uses precomputed summaries delivered with the main politicians payload for instant loading.
        if (!latestActivity && voteSummariesByTag) {
          const voteSummary = voteSummariesByTag[tag];
          if (voteSummary && voteSummary.voteDate) {
            const dateObj = parseLocalDate(voteSummary.voteDate);
            latestActivity = {
              id: `vote-${voteSummary.voteDate}-${tag}`,
              type: 'vote',
              summary: `Voted "${voteSummary.voteText}" on ${voteSummary.displayTitle}`,
              date: voteSummary.voteDate,
              formattedDate: dateObj && !Number.isNaN(dateObj.getTime())
                ? dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                : null,
              tags: ['Voting Record', tag]
            };
          }
        }
      }

      return latestActivity;
    } catch (error) {
      console.error('Error in getLatestActivityForTag:', error, { politicianId, tag });
      return null;
    }
  };

  // Function to get the latest update date for a politician
  const getLatestUpdateDate = (politicianId, tag) => {
    try {
      const latestActivity = getLatestActivityForTag(politicianId, tag);
      if (!latestActivity || !latestActivity.date) {
        return new Date(0); // Return epoch date for politicians with no updates
      }
      return parseLocalDate(latestActivity.date) || new Date(0);
    } catch (error) {
      console.error('Error in getLatestUpdateDate:', error, { politicianId, tag });
      return new Date(0);
    }
  };

  // Function to fetch voting records for a politician
  const fetchPoliticianVotingRecord = async (politicianId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/politician-voting-record/id/${encodeURIComponent(politicianId)}`));
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching voting record:', error);
      return null;
    }
  };

  const fetchPoliticianVoteCount = async (politicianId) => {
    const record = await fetchPoliticianVotingRecord(politicianId);
    return record?.voting_stats?.total_votes ?? (record?.votes?.length || 0);
  };

  // HYPER-AGGRESSIVE: Fetch pre-processed vote summaries from backend
  const fetchVoteSummaries = async (politicianName) => {
    try {
      const response = await fetch(buildApiUrl(`/api/politician-vote-summaries/${encodeURIComponent(politicianName)}`));
      if (response.ok) {
        const data = await response.json();
        return data.voteSummaries;
      }
      return null;
    } catch (error) {
      console.error('Error fetching vote summaries:', error);
      return null;
    }
  };

  // HYPER-AGGRESSIVE: Simple on-demand fetch using backend pre-processing
  const preloadVotingRecords = async () => {
    const politiciansWithVotes = politicians.filter(hasVotingRecords);

    // HYPER-AGGRESSIVE: Fetch vote summaries in parallel for instant access
    const promises = politiciansWithVotes.map(async (politician) => {
      try {
        console.log(`⚡ HYPER-AGGRESSIVE: Fetching vote summaries for ${politician.name}`);
        const voteSummaries = await fetchVoteSummaries(politician.name);

        if (voteSummaries) {
          setPoliticianVoteSummaries(prev => ({
            ...prev,
            [politician.id]: voteSummaries
          }));
          console.log(`✅ HYPER-AGGRESSIVE: Cached vote summaries for ${politician.name}`);
        }
      } catch (error) {
        console.error(`Error fetching vote summaries for ${politician.name}:`, error);
      }
    });

    // Execute all fetches in parallel
    await Promise.all(promises);
  };

  // Function to generate appropriate summary text based on vote type
  const getVoteSummaryText = (voteText, billTitle) => {
    const vote = voteText.toLowerCase();

    if (vote === 'nv' || vote === 'not voted') {
      return `Recorded as Not Voting on ${billTitle}`;
    } else if (vote === 'absent') {
      return `Absent for vote on ${billTitle}`;
    } else {
      return `Voted "${voteText}" on ${billTitle}`;
    }
  };

  // Function to get the most recent vote for a specific tag/policy area (using pre-loaded data)
  const getLatestVoteForTag = (politicianId, tag) => {
    const voteSummaries = politicianVoteSummaries[politicianId];

    if (!voteSummaries) {
      return null;
    }

    // If tag is 'all', get the most recent vote from any tag
    if (tag === 'all') {
      const allVotes = Object.values(voteSummaries).filter(vote => vote);
      if (allVotes.length === 0) return null;

      // Sort by date and get the most recent
      const mostRecent = allVotes.sort((a, b) => {
        const dateA = parseLocalDate(a.voteDate) || new Date(0);
        const dateB = parseLocalDate(b.voteDate) || new Date(0);
        return dateB - dateA;
      })[0];

      return {
        id: `vote-${mostRecent.voteDate}`,
        type: 'vote',
        vote: mostRecent.voteText,
        billTitle: mostRecent.displayTitle,
        voteDate: mostRecent.voteDate,
        date: mostRecent.voteDate,
        formattedDate: (() => {
          const dateObj = parseLocalDate(mostRecent.voteDate);
          return dateObj && !Number.isNaN(dateObj.getTime())
            ? dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })
            : null;
        })(),
        summary: getVoteSummaryText(mostRecent.voteText, mostRecent.displayTitle),
        tags: ['Voting Record']
      };
    }

    // For specific tags, get the pre-loaded vote summary
    const voteSummary = voteSummaries[tag];

    if (!voteSummary) {
      return null;
    }

    return {
      id: `vote-${voteSummary.voteDate}`,
      type: 'vote',
      vote: voteSummary.voteText,
      billTitle: voteSummary.displayTitle,
      voteDate: voteSummary.voteDate,
      date: voteSummary.voteDate,
      formattedDate: (() => {
        const dateObj = parseLocalDate(voteSummary.voteDate);
        return dateObj && !Number.isNaN(dateObj.getTime())
          ? dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })
          : null;
      })(),
      summary: getVoteSummaryText(voteSummary.voteText, voteSummary.displayTitle),
      tags: ['Voting Record']
    };
  };

  // Function to navigate directly to bill detail page
  const handleNavigateToBill = (billNumber) => {
    // Remove spaces from bill number in URL for cleaner URLs (e.g., "SB 120" -> "SB120")
    const cleanBillNumber = billNumber.replace(/\s+/g, '');
    navigate(`/bills/${cleanBillNumber}`);
    setCurrentView('bills');
    setSelectedPolitician(null);
  };

  // Get branch classification from database - no hardcoding
  const getBranchClassification = (office, overrideBranch = null) => {
    // If we have a stored branch from the database, use it
    if (overrideBranch) {
      return {
        branch: overrideBranch,
        color: isDarkMode ? 'bg-gray-700/50 text-gray-300 border-gray-500/30' : 'bg-gray-100 text-gray-700 border-gray-200',
        icon: <Building className="w-4 h-4 mr-1" />
      };
    }

    // If no branch data, return null (will show N/A)
    return null;
  };

  // Get locality level from database - no hardcoding
  const getLocalityLevel = (office) => {
    // This function is no longer needed since locality comes from the database
    // Return null to indicate no hardcoded classification
    return null;
  };

  // Helper function to group politicians
  const groupPoliticians = (politicians, groupBy) => {
    if (groupBy === GROUPING_OPTIONS.NONE) {
      return { 'All Representatives': politicians };
    }

    const groups = {};

    politicians.forEach(politician => {
      let groupKey;

      switch (groupBy) {
        case GROUPING_OPTIONS.BRANCH:
          const branchInfo = getBranchClassification(politician.office, politician.branch || null);
          groupKey = branchInfo ? branchInfo.branch : DEFAULT_VALUES.NA;
          break;
        case GROUPING_OPTIONS.OFFICE:
          let office = politician.office || DEFAULT_VALUES.NA;
          // Extract base office title by removing post/district/at-large designations
          let baseOffice = office
            .replace(/\s+(post|district|at\s*large)\s*\d*$/i, '') // Remove "Post 1", "District 2", "At Large"
            .replace(/\s+(post|district|at\s*large)$/i, '') // Remove standalone "Post", "District", "At Large"
            .replace(/\s+\([^)]*\)$/, '') // Remove parenthetical designations like "(Post 1)"
            .trim();

          // If the base office is empty after cleaning, use the original
          groupKey = baseOffice || office;
          break;
        case GROUPING_OPTIONS.LOCALITY:
          groupKey = politician.locality || DEFAULT_VALUES.NA;
          break;
        default:
          groupKey = 'All Representatives';
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(politician);
    });

    // Sort groups alphabetically - no hardcoded ordering
    const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b));

    const sortedGroups = {};
    sortedKeys.forEach(key => {
      sortedGroups[key] = groups[key];
    });

    return sortedGroups;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading Georgia representatives and policy tags...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchPoliticians}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }


  if (currentView === 'profile' && selectedPolitician) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
            {/* Mobile header: icon-only back, centered title, dark mode on right */}
            <div className="flex items-center justify-between md:hidden">
              <button
                onClick={handleBackToHome}
                className={`hidden p-2 ${getPartyButtonClass(selectedPolitician.party, true)} rounded-lg transition-all duration-200`}
                aria-label="Back to Home"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-0">
                <Link to="/about" className="inline-flex items-center">
                  <span className="font-bold text-2xl select-none" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <span className="text-[#3b82f6]">Civic</span>
                    <span className="text-[#ef4444]">Duty</span>
                  </span>
                </Link>
              </h1>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className={`p-2 rounded-lg transition-all duration-200 ${isDarkMode
                      ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  aria-label="Toggle dark mode"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                </button>
                <UserMenu isDarkMode={isDarkMode} />
              </div>
            </div>

            {/* Desktop header */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBackToHome}
                  className={`flex items-center px-4 py-2 ${getPartyButtonClass(selectedPolitician.party, true)} rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-x-1`}
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  <span>Back to Home</span>
                </button>
                <div className="flex items-center space-x-3">
                  <h1 className="text-0">
                    <Link to="/about" className="inline-flex items-center">
                      <span className="font-bold text-3xl md:text-4xl select-none" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <span className="text-[#3b82f6]">Civic</span>
                        <span className="text-[#ef4444]">Duty</span>
                      </span>
                    </Link>
                  </h1>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className={`p-2 rounded-lg transition-all duration-200 ${isDarkMode
                      ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  aria-label="Toggle dark mode"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                </button>
                <UserMenu isDarkMode={isDarkMode} />
              </div>
            </div>
          </div>
        </header>

        <DevelopmentBanner isDarkMode={isDarkMode} />

        <div className="flex flex-col md:flex-row h-[calc(100vh-80px)]">
          <div className={`w-full md:w-2/5 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} md:border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} flex flex-col`}>
            {/* Mobile top layout: profile left, quick info right */}
            <div className="block md:hidden px-4 py-4 border-b border-gray-700/20">
              <div className="grid grid-cols-2 gap-4 items-start">
                <div className="text-center self-center">
                  <div className="relative inline-block mb-1">
                    <img
                      src={selectedPolitician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedPolitician.name)}&background=random&color=fff`}
                      alt={selectedPolitician.name}
                      className={`w-24 h-24 rounded-full object-cover border-4 mb-1 ${getPartyBorderClass(selectedPolitician.party)}`}
                    />
                  </div>
                  <h1 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} leading-tight`}>
                    {selectedPolitician.name}
                  </h1>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>{selectedPolitician.office || FALLBACK_MESSAGES.NO_OFFICE_INFO}</p>
                  <span className={`mt-2 px-2.5 py-1 rounded-full text-xs font-medium ${getPartyColor(selectedPolitician.party)} inline-block`}>
                    {selectedPolitician.party || FALLBACK_MESSAGES.NO_PARTY_INFO}
                  </span>
                </div>
                <div className="space-y-3">
                  {(() => {
                    const accounts = (selectedPolitician.socialAccounts || []).filter((a) => a.account_url);
                    if (accounts.length > 0) {
                      return (
                        <div className={`p-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          <h3 className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 uppercase tracking-wide flex items-center flex-shrink-0`}>
                            <Share2 className="w-3 h-3 mr-1" />
                            Social & Campaign Links
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {accounts.map((acc) => {
                              const platform = (acc.platform || '').toLowerCase();
                              const label = platform.includes('twitter') || platform === 'x' ? 'X / Twitter' : platform.includes('facebook') ? 'Facebook' : platform.includes('instagram') ? 'Instagram' : platform.includes('youtube') ? 'YouTube' : platform.includes('campaign') ? 'Campaign Website' : (acc.platform || 'Website').replace(/^\w/, (c) => c.toUpperCase());
                              const IconComponent = getSocialLinkIcon(platform);
                              return (
                                <a
                                  key={acc.id}
                                  href={acc.account_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => trackEvent('politician_external_link_clicked', { politicianId: selectedPolitician.id, politicianName: selectedPolitician.name, platform: platform || 'unknown', url: acc.account_url, source: 'political_profile_left_column' })}
                                  className={`min-w-[calc(33.333%-0.334rem)] flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl text-xs font-medium text-white transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg min-h-[2.75rem] ${getPartyWebsiteButtonClass(selectedPolitician.party)}`}
                                  aria-label={`Open ${selectedPolitician.name}'s ${label} profile`}
                                >
                                  <IconComponent className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="truncate">{label}</span>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-red-50'} rounded-lg p-3 border ${isDarkMode ? 'border-gray-700' : 'border-red-200'}`}>
                        <div className="flex items-start gap-2 mb-2">
                          <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex-1`}>
                            Want your representative&apos;s full profile on CivicDuty? Contact them and ask them to request a profile—we will consolidate all of their official social media links and campaign links into this profile alongside filling out their about page.
                          </p>
                          <InfoTooltip
                            content="Our long-term goal is to create Political Profiles for all legislators in the assembly, but due to our beta status and small team size, we can only work with legislators who have reached out to us directly."
                            isDarkMode={isDarkMode}
                            size="md"
                          >
                            <HelpCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} cursor-help`} />
                          </InfoTooltip>
                        </div>
                        <p className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                          For legislators, please see <Link to="/about#for-legislators" className="underline font-medium text-[#ef4444] hover:text-[#dc2626]">here</Link>. Legislators and staff: email us from your official address to request your profile.
                        </p>
                        {selectedPolitician.website && selectedPolitician.website !== '#' && selectedPolitician.website !== FALLBACK_MESSAGES.NO_WEBSITE ? (
                          <a
                            href={selectedPolitician.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full inline-flex items-center justify-center px-3 py-2 bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors font-medium text-xs"
                          >
                            Request Access
                          </a>
                        ) : (
                          <button
                            className="w-full px-3 py-2 bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors font-medium text-xs"
                            onClick={() => window.open('mailto:founders@civicduty.app?subject=Request Access for Political Profile', '_blank')}
                          >
                            Request Access
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  <div className={`rounded-xl overflow-hidden flex flex-col border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <button type="button" onClick={() => setAboutPersonExpanded((v) => !v)} className={`text-xs font-semibold ${getPartyWebsiteButtonClass(selectedPolitician.party)} text-white uppercase tracking-wide flex items-center w-full text-left px-3 py-3 cursor-pointer hover:opacity-90 transition-opacity`}>
                      <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      About the Person
                      <span className="ml-auto">{aboutPersonExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</span>
                    </button>
                    {aboutPersonExpanded && (
                      <div className={`flex-1 overflow-y-auto min-h-[4rem] max-h-32 p-2.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} ${selectedPolitician.party === 'Democrat'
                          ? 'democrat-scrollbar'
                          : selectedPolitician.party === 'Republican'
                            ? 'republican-scrollbar'
                            : 'custom-scrollbar'
                        }`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                          {selectedPolitician.aboutPerson || getEmptyBioMessage((selectedPolitician.socialAccounts || []).filter((a) => a.account_url).length === 0)}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className={`rounded-xl overflow-hidden flex flex-col border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <button type="button" onClick={() => setAboutPositionExpanded((v) => !v)} className={`text-xs font-semibold ${getPartyWebsiteButtonClass(selectedPolitician.party)} text-white uppercase tracking-wide flex items-center w-full text-left px-3 py-3 cursor-pointer hover:opacity-90 transition-opacity`}>
                      <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      About the Position
                      <span className="ml-auto">{aboutPositionExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</span>
                    </button>
                    {aboutPositionExpanded && (
                      <div className={`flex-1 overflow-y-auto min-h-[4rem] max-h-32 p-2.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} ${selectedPolitician.party === 'Democrat'
                          ? 'democrat-scrollbar'
                          : selectedPolitician.party === 'Republican'
                            ? 'republican-scrollbar'
                            : 'custom-scrollbar'
                        }`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                          {selectedPolitician.aboutPosition || getEmptyBioMessage((selectedPolitician.socialAccounts || []).filter((a) => a.account_url).length === 0)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer section with Represents */}
              <div className="mt-4">
                <div className={`flex items-center space-x-3 p-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl`}>
                  <div className={`w-8 h-8 ${getPartyGradientBg(selectedPolitician.party)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Represents</p>
                    <p className={`text-sm font-semibold truncate ${getPartyAccentClass(selectedPolitician.party)}`}>
                      {selectedPolitician.district
                        ? (/^district\s+/i.test(String(selectedPolitician.district).trim())
                            ? String(selectedPolitician.district).trim()
                            : `District ${String(selectedPolitician.district).trim()}`)
                        : (selectedPolitician.location || FALLBACK_MESSAGES.NO_LOCATION_INFO)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop header block */}
            <div className={`hidden md:block flex-shrink-0 ${isDarkMode ? 'bg-gray-900' : getPartyProfileBg(selectedPolitician.party)} px-8 py-5 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-50'}`}>
              <div className="text-center">
                <div className="relative inline-block mb-1">
                  <img
                    src={selectedPolitician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedPolitician.name)}&background=random&color=fff`}
                    alt={selectedPolitician.name}
                    className={`w-[7.5rem] h-[7.5rem] rounded-full object-cover border-4 mb-1 ${getPartyBorderClass(selectedPolitician.party)}`}
                  />
                </div>
                <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-1 leading-tight`}>
                  {selectedPolitician.name}
                </h1>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} font-medium mb-2`}>{selectedPolitician.office || FALLBACK_MESSAGES.NO_OFFICE_INFO}</p>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${getPartyColor(selectedPolitician.party)} inline-block`}>
                  {selectedPolitician.party || FALLBACK_MESSAGES.NO_PARTY_INFO}
                </span>
              </div>
            </div>

            <div className={`flex-1 px-4 md:px-8 py-4 md:py-6 space-y-4 md:space-y-6 overflow-y-auto ${selectedPolitician.party === 'Democrat'
                ? 'democrat-scrollbar'
                : selectedPolitician.party === 'Republican'
                  ? 'republican-scrollbar'
                  : 'custom-scrollbar'
              }`}>
              <div className="hidden md:grid grid-cols-1 gap-4">
                <div className={`flex items-center space-x-3 p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl`}>
                  <div className={`w-10 h-10 ${getPartyGradientBg(selectedPolitician.party)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Represents</p>
                    <p className={`text-lg font-semibold truncate ${getPartyAccentClass(selectedPolitician.party)}`}>
                      {selectedPolitician.district
                        ? (/^district\s+/i.test(String(selectedPolitician.district).trim())
                            ? String(selectedPolitician.district).trim()
                            : `District ${String(selectedPolitician.district).trim()}`)
                        : (selectedPolitician.location || FALLBACK_MESSAGES.NO_LOCATION_INFO)}
                    </p>
                  </div>
                </div>
              </div>

              {(() => {
                const desktopAccounts = (selectedPolitician.socialAccounts || []).filter((a) => a.account_url);
                if (desktopAccounts.length > 0) {
                  return (
                    <div className={`hidden md:block ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6 border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 uppercase tracking-wide flex items-center`}>
                        <Share2 className="w-4 h-4 mr-2" />
                        Social & Campaign Links
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {desktopAccounts.map((acc) => {
                          const platform = (acc.platform || '').toLowerCase();
                          const label = platform.includes('twitter') || platform === 'x' ? 'X / Twitter' : platform.includes('facebook') ? 'Facebook' : platform.includes('instagram') ? 'Instagram' : platform.includes('youtube') ? 'YouTube' : platform.includes('campaign') ? 'Campaign Website' : (acc.platform || 'Website').replace(/^\w/, (c) => c.toUpperCase());
                          const IconComponent = getSocialLinkIcon(platform);
                          return (
                            <a
                              key={acc.id}
                              href={acc.account_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackEvent('politician_external_link_clicked', { politicianId: selectedPolitician.id, politicianName: selectedPolitician.name, platform: platform || 'unknown', url: acc.account_url, source: 'political_profile_left_column' })}
                              className={`min-w-[calc(33.333%-0.334rem)] flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-medium text-white transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg min-h-[3rem] ${getPartyWebsiteButtonClass(selectedPolitician.party)}`}
                              aria-label={`Open ${selectedPolitician.name}'s ${label} profile`}
                            >
                              <IconComponent className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{label}</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return (
                  <div className={`hidden md:block ${isDarkMode ? 'bg-gray-800' : 'bg-red-50'} rounded-lg p-4 border ${isDarkMode ? 'border-gray-700' : 'border-red-200'}`}>
                    <div className="flex items-start gap-2 mb-3">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex-1`}>
                        Want your representative&apos;s full profile on CivicDuty? Contact them and ask them to request a profile—we will consolidate all of their official social media links and campaign links into this profile alongside filling out their about page.
                      </p>
                      <InfoTooltip
                        content="Our long-term goal is to create Political Profiles for all legislators in the assembly, but due to our beta status and small team size, we can only work with legislators who have reached out to us directly."
                        isDarkMode={isDarkMode}
                        size="md"
                      >
                        <HelpCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} cursor-help`} />
                      </InfoTooltip>
                    </div>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                      For legislators, please see <Link to="/about#for-legislators" className="underline font-medium text-[#ef4444] hover:text-[#dc2626]">here</Link>. Legislators and staff: email us from your official address to request your profile.
                    </p>
                    {selectedPolitician.website && selectedPolitician.website !== '#' && selectedPolitician.website !== FALLBACK_MESSAGES.NO_WEBSITE ? (
                      <a
                        href={selectedPolitician.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center px-4 py-2 bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors font-medium"
                      >
                        Request Access
                      </a>
                    ) : (
                      <button
                        className="w-full px-4 py-2 bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors font-medium"
                        onClick={() => window.open('mailto:founders@civicduty.app?subject=Request Access for Political Profile', '_blank')}
                      >
                        Request Access
                      </button>
                    )}
                  </div>
                );
              })()}

              <div className={`hidden md:block rounded-xl overflow-hidden border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <button type="button" onClick={() => setAboutPersonExpanded((v) => !v)} className={`text-sm font-semibold ${getPartyWebsiteButtonClass(selectedPolitician.party)} text-white uppercase tracking-wide flex items-center w-full text-left px-6 py-4 cursor-pointer hover:opacity-90 transition-opacity`}>
                  <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  About the Person
                  <span className="ml-auto">{aboutPersonExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                </button>
                {aboutPersonExpanded && (
                  <div className={`p-5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                      {selectedPolitician.aboutPerson || getEmptyBioMessage((selectedPolitician.socialAccounts || []).filter((a) => a.account_url).length === 0)}
                    </p>
                  </div>
                )}
              </div>

              <div className={`hidden md:block rounded-xl overflow-hidden border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <button type="button" onClick={() => setAboutPositionExpanded((v) => !v)} className={`text-sm font-semibold ${getPartyWebsiteButtonClass(selectedPolitician.party)} text-white uppercase tracking-wide flex items-center w-full text-left px-6 py-4 cursor-pointer hover:opacity-90 transition-opacity`}>
                  <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  About the Position
                  <span className="ml-auto">{aboutPositionExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                </button>
                {aboutPositionExpanded && (
                  <div className={`p-5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                      {selectedPolitician.aboutPosition || getEmptyBioMessage((selectedPolitician.socialAccounts || []).filter((a) => a.account_url).length === 0)}
                    </p>
                  </div>
                )}
              </div>


            </div>

            {selectedPolitician.website && selectedPolitician.website !== '#' && selectedPolitician.website !== FALLBACK_MESSAGES.NO_WEBSITE && (
              <div className="px-8 pb-6">
                <a
                  href={selectedPolitician.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full inline-flex items-center justify-center px-6 py-3 text-white font-medium rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${getPartyWebsiteButtonClass(selectedPolitician.party)}`}
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Visit Official Website
                </a>
              </div>
            )}

          </div>

          <div className={`w-full md:w-3/5 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex flex-col`}>
            {/* Tabs Header */}
            <div className={`flex-shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} px-8 py-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  {/* Updates Tab - visibility from position_history.profile_tabs */}
                  {showProfileTab(PROFILE_VIEW_MODES.UPDATES) && (
                    <button
                      onClick={() => setProfileViewMode(PROFILE_VIEW_MODES.UPDATES)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${profileViewMode === PROFILE_VIEW_MODES.UPDATES
                          ? getPartyButtonClass(selectedPolitician.party, true)
                          : isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                    >
                      <Bell className="w-5 h-5" />
                      <span className="font-medium">Updates</span>
                    </button>
                  )}

                  {/* Committees Tab - visibility from profile_tabs */}
                  {showProfileTab('committees') && (
                    <button
                      onClick={() => setProfileViewMode(PROFILE_VIEW_MODES.COMMITTEES)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${profileViewMode === PROFILE_VIEW_MODES.COMMITTEES
                          ? getPartyButtonClass(selectedPolitician.party, true)
                          : isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                    >
                      <Users className="w-5 h-5" />
                      <span className="font-medium">Committees</span>
                    </button>
                  )}

                  {/* Sponsored Legislation Tab - visibility from profile_tabs */}
                  {showProfileTab('sponsored') && (
                    <button
                      onClick={() => setProfileViewMode(PROFILE_VIEW_MODES.SPONSORED)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${profileViewMode === PROFILE_VIEW_MODES.SPONSORED
                          ? getPartyButtonClass(selectedPolitician.party, true)
                          : isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                    >
                      <FileText className="w-5 h-5" />
                      <span className="font-medium">Sponsored Legislation</span>
                    </button>
                  )}

                  {/* Voting Records Tab - visibility driven only by profile_tabs */}
                  {showProfileTab('voting') && (
                    <button
                      onClick={() => setProfileViewMode('voting')}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${profileViewMode === 'voting'
                          ? getPartyButtonClass(selectedPolitician.party, true)
                          : isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                    >
                      <Vote className="w-5 h-5" />
                      <span className="font-medium">Voting Records</span>
                    </button>
                  )}

                  {/* Social Media Tab - visibility from position_history.profile_tabs */}
                  {showProfileTab(PROFILE_VIEW_MODES.SOCIAL_MEDIA) && (
                    <button
                      onClick={() => setProfileViewMode(PROFILE_VIEW_MODES.SOCIAL_MEDIA)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${profileViewMode === PROFILE_VIEW_MODES.SOCIAL_MEDIA
                          ? getPartyButtonClass(selectedPolitician.party, true)
                          : isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span className="font-medium">Social Media</span>
                    </button>
                  )}
                </div>

                {/* Desktop: LIVE indicator in same row as tabs */}
                {profileViewMode === PROFILE_VIEW_MODES.UPDATES && (
                  <div className="hidden md:flex items-center space-x-2">
                    <div className="relative flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg ring-2 ring-green-400"></span>
                    </div>
                    <span className="text-sm font-bold text-green-700 tracking-wide uppercase drop-shadow-sm animate-pulse">
                      Live
                    </span>
                  </div>
                )}

              </div>

              {/* Content-specific headers */}
              {profileViewMode === PROFILE_VIEW_MODES.UPDATES && (
                <>
                  <div className="mb-4">
                    {/* Mobile: LIVE indicator in same row as header */}
                    <div className="md:hidden flex items-center justify-between mb-2">
                      <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Recent Updates
                      </h2>
                      {/* Live indicator */}
                      <div className="flex items-center space-x-2">
                        <div className="relative flex items-center">
                          <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg ring-2 ring-green-400"></span>
                        </div>
                        <span className="text-sm font-bold text-green-700 tracking-wide uppercase drop-shadow-sm animate-pulse">
                          Live
                        </span>
                      </div>
                    </div>

                    {/* Desktop: Separate header and LIVE indicator */}
                    <div className="hidden md:block">
                      <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Recent Updates
                      </h2>
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                        Latest activity and policy positions
                      </p>
                    </div>

                  </div>

                  <div className={`flex flex-wrap gap-2 max-h-32 overflow-y-auto ${selectedPolitician.party === 'Democrat'
                      ? 'democrat-scrollbar'
                      : selectedPolitician.party === 'Republican'
                        ? 'republican-scrollbar'
                        : 'custom-scrollbar'
                    }`}>
                    {(() => {
                      const { allTag, sortedTags } = getSortedTagsForPolitician(selectedPolitician.id);
                      // Show 3 tags on mobile, 4 on desktop
                      const visibleTags = showMoreTags ? sortedTags : sortedTags.slice(0, 4);
                      const hasMoreTags = sortedTags.length > 4;

                      return (
                        <>
                          {/* All tag - always first */}
                          <button
                            onClick={() => setSelectedTags([])}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedTags.length === 0
                                ? getPartyButtonClass(selectedPolitician.party, true)
                                : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                              } flex items-center gap-2`}
                          >
                            {allTag.name}
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${selectedTags.length === 0
                                ? 'bg-white text-gray-800'
                                : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                              }`}>
                              {allTag.count}
                            </span>
                          </button>

                          {/* Sorted tags by frequency */}
                          {visibleTags.map((tag, index) => (
                            <button
                              key={tag.id || tag._id}
                              onClick={() => handleTagToggle(tag.name)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedTags.includes(tag.name)
                                  ? getPartyButtonClass(selectedPolitician.party, true)
                                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                                } flex items-center gap-2 ${index === 3 ? 'hidden md:flex' : ''}`}
                            >
                              {tag.name}
                              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${selectedTags.includes(tag.name)
                                  ? 'bg-white text-gray-800'
                                  : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                }`}>
                                {tag.count}
                              </span>
                            </button>
                          ))}

                          {/* Show more/less button - appears when more than 3 tags on mobile, more than 4 on desktop */}
                          {(sortedTags.length > 3) && (
                            <button
                              onClick={() => setShowMoreTags(!showMoreTags)}
                              className={`p-2 rounded-lg text-xs font-medium transition-all ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                } flex items-center justify-center`}
                            >
                              <svg
                                className={`w-4 h-4 transition-transform ${showMoreTags ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              )}

              {profileViewMode === 'voting' && (
                <div className="mb-4">
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Voting Records
                  </h2>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                    Complete legislative voting history and statistics
                  </p>
                </div>
              )}

              {profileViewMode === 'sponsored' && (
                <div className="mb-4">
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Sponsored Legislation
                  </h2>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                    Bills this member has authored or co-sponsored
                  </p>
                </div>
              )}
              
              {profileViewMode === 'committees' && (
                <div className="mb-4">
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Committees
                  </h2>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                    Committees this member is assigned to
                  </p>
                </div>
              )}
            </div>

            <div className={`flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-6 ${selectedPolitician.party === 'Democrat'
                ? 'democrat-scrollbar'
                : selectedPolitician.party === 'Republican'
                  ? 'republican-scrollbar'
                  : 'custom-scrollbar'
              }`}>
              {/* Updates Content */}
              {profileViewMode === PROFILE_VIEW_MODES.UPDATES && (
                <>
                  {updatesLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading updates...</p>
                      </div>
                    </div>
                  ) : currentUpdates.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <Bell className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
                        <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>No updates found</h3>
                        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                          {selectedTags.length === 0 ? 'No approved updates are available yet.' : `No updates found for the selected tags.`}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {currentUpdates.map((update) => (
                        <div
                          key={update.id}
                          onClick={(e) => handleUpdateClick(update, e)}
                          className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 md:p-6 border overflow-hidden ${selectedPolitician.party === 'Republican'
                              ? isDarkMode ? 'border-gray-800 hover:border-red-800/50' : 'hover:border-red-200'
                              : isDarkMode ? 'border-gray-800 hover:border-blue-800/50' : 'hover:border-blue-200'
                            } hover:shadow-lg transition-all duration-200 cursor-pointer group`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className={`flex items-center space-x-2 text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                              <span>{update.formattedDate}</span>
                            </div>
                            <div className="flex flex-wrap gap-1 justify-end max-w-[60%] md:max-w-none">
                              {update.tags && update.tags.map((tag, index) => {
                                const IconComponent = getPolicyTagIcon(tag);
                                const tagColor = getPolicyTagColor(tag, isDarkMode);
                                return (
                                  <span key={index} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${tagColor} shadow-sm whitespace-nowrap`}>
                                    <IconComponent className="w-3 h-3 mr-1" />
                                    {tag}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                          <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'} leading-relaxed font-medium break-words ${selectedPolitician.party === 'Republican'
                              ? isDarkMode ? 'group-hover:text-red-300' : 'group-hover:text-red-900'
                              : isDarkMode ? 'group-hover:text-blue-300' : 'group-hover:text-blue-900'
                            } transition-colors`}>
                            {update.summary}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Voting Records Content */}
              {profileViewMode === 'voting' && (
                <VotingRecords
                  politician={selectedPolitician}
                  isDarkMode={isDarkMode}
                  getPartyColor={getPartyColor}
                  getPartyButtonClass={getPartyButtonClass}
                  getPartyGradientBg={getPartyGradientBg}
                  onNavigateToBill={handleNavigateToBill}
                  selectedTag={selectedTag}
                />
              )}

              {/* Committees Content */}
              {profileViewMode === PROFILE_VIEW_MODES.COMMITTEES && (
                <div className="space-y-4">
                  {committeesLoading && (!politicianCommittees[selectedPolitician.id] || politicianCommittees[selectedPolitician.id].length === 0) ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading committees...</p>
                      </div>
                    </div>
                  ) : (politicianCommittees[selectedPolitician.id] || []).length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <Users className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
                        <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>No committee assignments found</h3>
                        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                          This member is not currently listed on any committees.
                        </p>
                      </div>
                    </div>
                  ) : (
                    (politicianCommittees[selectedPolitician.id] || []).map((committee, idx) => (
                      <CommitteeCard
                        key={committee.id || idx}
                        committee={committee}
                        isDarkMode={isDarkMode}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Sponsored Legislation Content */}
              {profileViewMode === PROFILE_VIEW_MODES.SPONSORED && (
                <SponsoredBills
                  politician={selectedPolitician}
                  isDarkMode={isDarkMode}
                  onNavigateToBill={handleNavigateToBill}
                />
              )}

              {/* Social Media Content */}
              {profileViewMode === PROFILE_VIEW_MODES.SOCIAL_MEDIA && (
                <SocialMediaPosts
                  politician={selectedPolitician}
                  isDarkMode={isDarkMode}
                />
              )}
            </div>
          </div>
        </div>

        {expandedUpdate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-in fade-in duration-300">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto modal-scrollbar shadow-2xl transform transition-all duration-300 scale-100 animate-in slide-in-from-bottom-4`}>
              <div className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} p-6 rounded-t-2xl`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Update Details</h2>
                  </div>
                  <button
                    onClick={handleCloseModal}
                    className={`${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors p-2 hover:bg-gray-100 rounded-full`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-6`}>
                  <Calendar className="w-4 h-4" />
                  <span>{expandedUpdate.formattedDate}</span>
                </div>

                <div className="prose prose-lg max-w-none">
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-black'} leading-relaxed text-lg mb-6 font-semibold`}>
                    {expandedUpdate.fullSummary}
                  </p>
                </div>

                {(() => {
                  let tagsToShow = Array.isArray(expandedUpdate.tags) && expandedUpdate.tags.length > 0
                    ? expandedUpdate.tags.filter(Boolean)
                    : (expandedUpdate.category ? [expandedUpdate.category] : []);
                  return tagsToShow.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-2 mb-6">
                      {tagsToShow.map((tag, idx) => {
                        const IconComponent = getPolicyTagIcon(tag);
                        const tagColor = getPolicyTagColor(tag, isDarkMode);
                        return (
                          <span key={idx} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${tagColor} shadow-sm`}>
                            <IconComponent className="w-3 h-3 mr-1" />
                            {tag}
                          </span>
                        );
                      })}
                    </div>
                  ) : null;
                })()}

                <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} pt-6`}>
                  {expandedUpdate.pressReleaseUrl && expandedUpdate.pressReleaseUrl !== '#' ? (
                    <a
                      href={expandedUpdate.pressReleaseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-blue-600 text-white font-medium rounded-xl hover:from-red-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Read Full Press Release
                    </a>
                  ) : (
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} italic`}>Original press release not available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (featureControlsLoading) {
    return null;
  }

  return (
    <>
      <ScrollToTop />
      <BugReport isDarkMode={isDarkMode} />
      <Routes>
      <Route path="/account" element={resolvedFeatureControls.accounts ? <Account isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} /> : <Navigate to={fallbackRoute} replace />} />
      <Route path="/reset-password" element={resolvedFeatureControls.accounts ? <ResetPassword /> : <Navigate to={fallbackRoute} replace />} />
      <Route path="/signin" element={resolvedFeatureControls.accounts ? <SignInModal isDarkMode={isDarkMode} onToggleDarkMode={() => setIsDarkMode(!isDarkMode)} /> : <Navigate to={fallbackRoute} replace />} />
      <Route path="/" element={
        resolvedFeatureControls.home ? <Homepage
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          featureControls={resolvedFeatureControls}
          politicians={politicians}
          policyTags={availableTags}
          getPartyColor={getPartyColor}
          getPartyAccentClass={getPartyAccentClass}
          getPartyBorderClass={getPartyBorderClass}
          getBranchClassification={getBranchClassification}
          getLatestActivityForTag={getLatestActivityForTag}
          handlePoliticianClick={handlePoliticianClick}
          DEFAULT_VALUES={DEFAULT_VALUES}
          FALLBACK_MESSAGES={FALLBACK_MESSAGES}
          navigate={navigate}
        />
        : <Navigate to={fallbackRoute} replace />
      } />
      <Route path="/politicians/:politicianId" element={resolvedFeatureControls.politicalProfiles ? <PoliticianProfile /> : <Navigate to={fallbackRoute} replace />} />
      <Route path="/calendar" element={
        resolvedFeatureControls.calendar ? <CalendarPage
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          featureControls={resolvedFeatureControls}
        />
        : <Navigate to={fallbackRoute} replace />
      } />
      <Route path="/about" element={
        resolvedFeatureControls.about ? <About
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          featureControls={resolvedFeatureControls}
        />
        : <Navigate to={fallbackRoute} replace />
      } />
      <Route path="/local-body/:bodySlug" element={
        resolvedFeatureControls.localBody ? <LocalBody
          onBackToHome={handleBackToHome}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          featureControls={resolvedFeatureControls}
        />
        : <Navigate to={fallbackRoute} replace />
      } />
      {/* Redirect old route to default body (Warner Robins) */}
      <Route path="/local-body-verdicts" element={
        resolvedFeatureControls.localBody ? <LocalBody
          onBackToHome={handleBackToHome}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          featureControls={resolvedFeatureControls}
        />
        : <Navigate to={fallbackRoute} replace />
      } />
      <Route path="/bills" element={
        resolvedFeatureControls.bills ? <BillDetailWithBackground 
          onBackToHome={handleBackToHome}
          isDarkMode={isDarkMode} 
          setIsDarkMode={setIsDarkMode}
          featureControls={resolvedFeatureControls}
        />
        : <Navigate to={fallbackRoute} replace />
      } />
      <Route path="/bills/:billNumber" element={
        resolvedFeatureControls.bills ? <BillDetailWithBackground 
          onBackToHome={handleBackToHome}
          isDarkMode={isDarkMode} 
          setIsDarkMode={setIsDarkMode}
          featureControls={resolvedFeatureControls}
        />
        : <Navigate to={fallbackRoute} replace />
      } />
      <Route path="/politicians" element={
        <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <AppNavbar isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} featureControls={resolvedFeatureControls} />

          <DevelopmentBanner isDarkMode={isDarkMode} />

          <section className="py-4 md:py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-4 md:p-6 border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="text-center mb-4">
                  <h3 className={`text-base md:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2 inline-flex items-center gap-2 justify-center`}>
                    Political Profiles
                    <InfoTooltip
                      content="Policy topics show what issues each politician has been working on. Click a topic to see which representatives have recent activity in that area."
                      title="Policy Topics"
                      size="sm"
                      position="right"
                      iconClassName="opacity-70"
                    >
                      <span></span>
                    </InfoTooltip>
                  </h3>
                  <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Select a topic to see representatives with recent activity in that area
                  </p>
                </div>

                {/* Tag filtering buttons - Mobile optimized */}
                <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 max-h-24 md:max-h-32 overflow-y-auto custom-scrollbar">
                  <button
                    onClick={() => handleHomepageTagSelect('all')}
                    className={`px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg text-xs font-medium transition-all ${selectedHomepageTag === 'all'
                        ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                        : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                      }`}
                  >
                    All Topics
                  </button>
                  {availableTags.map(tag => (
                    <button
                      key={tag.id || tag._id}
                      onClick={() => handleHomepageTagSelect(tag.name)}
                      className={`px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${selectedHomepageTag === tag.name
                          ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                          : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                        }`}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>

                {/* Grouping controls */}
                <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col space-y-3 md:space-y-0 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-2">
                      <Users className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Group by:
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 md:gap-2">
                      <button
                        onClick={() => setGroupBy(GROUPING_OPTIONS.NONE)}
                        className={`px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${groupBy === GROUPING_OPTIONS.NONE
                            ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                          }`}
                      >
                        None
                      </button>
                      <button
                        onClick={() => setGroupBy(GROUPING_OPTIONS.BRANCH)}
                        className={`px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${groupBy === GROUPING_OPTIONS.BRANCH
                            ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                          }`}
                      >
                        <Gavel className="w-3 h-3 md:w-4 md:h-4 mr-1 inline" />
                        Branch
                      </button>
                      <button
                        onClick={() => setGroupBy(GROUPING_OPTIONS.LOCALITY)}
                        className={`px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${groupBy === GROUPING_OPTIONS.LOCALITY
                            ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                          }`}
                      >
                        <MapPin className="w-3 h-3 md:w-4 md:h-4 mr-1 inline" />
                        Level
                      </button>
                      <button
                        onClick={() => setGroupBy(GROUPING_OPTIONS.OFFICE)}
                        className={`px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${groupBy === GROUPING_OPTIONS.OFFICE
                            ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                          }`}
                      >
                        <Building className="w-3 h-3 md:w-4 md:h-4 mr-1 inline" />
                        Office
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {PROFILES_ENABLED && (
          <section className="py-4 md:py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {filteredPoliticians.length === 0 ? (
                <div className="text-center py-12">
                  <div className={`w-16 h-16 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Users className={`w-8 h-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                  <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>
                    No representatives found
                  </h3>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                    {selectedHomepageTag === 'all'
                      ? 'No representatives match your search criteria.'
                      : `No representatives have recent activity in "${selectedHomepageTag}".`
                    }
                  </p>
                  <button
                    onClick={() => {
                      setSelectedHomepageTag('all');
                      setSearchTerm('');
                    }}
                    className="mt-4 px-4 py-2 bg-gradient-to-r from-red-600 to-blue-600 text-white rounded-lg hover:from-red-700 hover:to-blue-700 transition-all"
                  >
                    Show All Representatives
                  </button>
                </div>
              ) : (
                (() => {
                  const groupedPoliticians = groupPoliticians(
                    filteredPoliticians,
                    groupBy
                  );

                  return (
                    <div className="space-y-8">
                      {Object.entries(groupedPoliticians).map(([groupName, politicians]) => (
                        <div key={groupName}>
                          {groupBy !== GROUPING_OPTIONS.NONE && (
                            <div className="mb-6">
                              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2 flex items-center gap-2`}>
                                {(() => {
                                  // Add appropriate icon based on group type
                                  if (groupBy === GROUPING_OPTIONS.BRANCH) {
                                    const branchInfo = getBranchClassification(politicians[0]?.office, politicians[0]?.branch || null);
                                    return branchInfo ? branchInfo.icon : <Building className="w-5 h-5" />;
                                  }
                                  if (groupBy === GROUPING_OPTIONS.LOCALITY) {
                                    // Use generic icon since we don't hardcode locality levels
                                    return <MapPin className="w-5 h-5" />;
                                  }
                                  if (groupBy === GROUPING_OPTIONS.OFFICE) {
                                    return <Building className="w-5 h-5" />;
                                  }
                                  return null;
                                })()}
                                {groupName}
                                <span className={`text-sm font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  ({politicians.length})
                                </span>
                              </h3>
                              <div className={`h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-4`}></div>
                            </div>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            {politicians.map((politician) => (
                              <PoliticianCard
                                key={politician.id}
                                politician={politician}
                                selectedHomepageTag={selectedHomepageTag}
                                isDarkMode={isDarkMode}
                                getPartyColor={getPartyColor}
                                getPartyAccentClass={getPartyAccentClass}
                                getPartyBorderClass={getPartyBorderClass}
                                getBranchClassification={getBranchClassification}
                                getLatestActivityForTag={getLatestActivityForTag}
                                handlePoliticianClick={handlePoliticianClick}
                                DEFAULT_VALUES={DEFAULT_VALUES}
                                FALLBACK_MESSAGES={FALLBACK_MESSAGES}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
          </section>
          )}
        </div>
      } />
      <Route path="*" element={<Navigate to={fallbackRoute} replace />} />
      </Routes>
    </>
  );
};

export default CivicDutyApp;
